import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { AiIndexingStateEnum } from 'graphql-common';
import ChipIconCircle from '@lib/components/Chip/ChipIconCircle';
import IndexingStatusMessages from '@lib/components/IndexingStatusMessages/IndexingStatusMessages';
import LinkComponent, { LinkSizes } from '@lib/components/Link/Link';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import PercentageBarCell from '@lib/components/Table/commonComponents/PercentageBarCell/PercentageBarCell';
import Typography from '@lib/components/Typography/Typography';
import { Colors } from '@lib/enums/colors';
import { dateTimeFormatEU } from '@lib/enums/dateTime';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import formatApproxDuration from '@lib/utils/formatApproxDuration';
import formatCount from '@lib/utils/formatCount';
import getIndexingStatus from '@lib/utils/indexingStateCopy';
import { IndexingFailureReason } from '@lib/utils/indexingFailureReasonCopy';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import PlanInfoTooltipIcon from './PlanInfoTooltipIcon';
import PlanSectionTitle from './PlanSectionTitle';
import styles from './CompanyPlanView.module.scss';

// The card's own shape rather than the generated `AiIndexingCoverage`: the two
// callers read it from different queries (the tenant's own aggregate and the
// viewed company's field), and every field is optional here so a partial
// selection or an in-flight query still type-checks.
export interface AiIndexingCoverageData {
  indexed?: number | null;
  skipped?: number | null;
  stale?: number | null;
  pending?: number | null;
  failed?: number | null;
  total?: number | null;
  progress?: number | null;
  estimatedReindexSeconds?: number | null;
  indexingState?: AiIndexingStateEnum | null;
  lastIndexedAt?: string | null;
  lastIndexingAttemptAt?: string | null;
  parked?: number | null;
  parkedReasons?: readonly IndexingFailureReason[] | null;
}

export interface AiIndexingSectionProps {
  coverage?: AiIndexingCoverageData | null;
  // Scopes the parked-banner dismissal — one company's verdict must not silence
  // another's. See IndexingStatusMessages.
  storageId: string;
  onRetryParked: () => void;
  retryParkedLoading?: boolean;
  // Opens the parked documents behind the count. Only the own-company card can
  // offer it — see IndexingStatusMessages.
  onParkedClick?: () => void;
  // Opens the not-indexable / failed document lists behind their counts. Same
  // reasoning as onParkedClick: `documentations` is scoped to the signed-in
  // company, so only the own-company card can offer them.
  onSkippedClick?: () => void;
  onFailedClick?: () => void;
}

/**
 * How much of the company's documentation the assistant can actually search.
 *
 * The steady-state buckets, then the processed share — carrying beside it the
 * documents being re-embedded right now and how long that has left to run —
 * and, as notes under the bar, the two things the counts cannot say: whether
 * anything is still working the backlog, and whether some of it has been given
 * up on.
 */
export default function AiIndexingSection({
  coverage,
  storageId,
  onRetryParked,
  retryParkedLoading,
  onParkedClick,
  onSkippedClick,
  onFailedClick,
}: AiIndexingSectionProps) {
  const { t, i18n } = useTranslation();

  const staleCount = coverage?.stale;
  const lastIndexedAt = coverage?.lastIndexedAt;
  const lastIndexingAttemptAt = coverage?.lastIndexingAttemptAt;
  const parkedCount = coverage?.parked;

  // The server's verdict on whether anything is still working the backlog. The
  // buckets cannot answer that — a document that exhausts its retries never
  // leaves `stale`/`failed` — and neither can the subscription's rhythm, since
  // failures publish frames too.
  const indexingStatus = getIndexingStatus(
    coverage?.indexingState,
    parkedCount,
    t,
  );

  // Beside the bar: how many documents the re-index still has to get through,
  // and how long that is going to take. The count is the `stale` bucket — the
  // documents that WERE searchable and are being re-embedded after a
  // region/model switch, semantically distinct from `pending` (never indexed).
  // The ETA only joins it where the work is actually going to happen: the
  // server derives the estimate from `retryingSoon` alone, so it still returns
  // a non-zero number in STALLED and under the PAUSED_* brakes, where a time
  // remaining would promise progress that is not coming.
  const reindexEta = indexingStatus?.showsEta
    ? formatApproxDuration(coverage?.estimatedReindexSeconds, t)
    : '';

  return (
    <>
      <PlanSectionTitle
        title={
          <span className={styles.titleRow}>
            {t('document-indexing')}
            <PlanInfoTooltipIcon body={t('document-indexing-description')} />
          </span>
        }
      />
      <div className={entityViewStyles.infoItem}>
        <div className={entityViewStyles.infoItemLabel}>
          <ChipIconCircle color={Colors.Green} />
          <Typography variant="caption">{t('indexed')}</Typography>
        </div>
        <div className={entityViewStyles.infoItemContent}>
          <Typography variant="body">
            {formatCount(coverage?.indexed, i18n.language)}
          </Typography>
        </div>
      </div>
      <div className={entityViewStyles.infoItem}>
        <div className={entityViewStyles.infoItemLabel}>
          <ChipIconCircle color={Colors.Gray} />
          <Typography variant="caption">{t('ai-not-indexable')}</Typography>
        </div>
        <div
          className={classNames(
            entityViewStyles.infoItemContent,
            styles.infoItemContentSpread,
          )}
        >
          <Typography variant="body">
            {formatCount(coverage?.skipped, i18n.language)}
          </Typography>
          {!!coverage?.skipped && onSkippedClick && (
            <LinkComponent
              size={LinkSizes.small}
              data-test-id="ai-indexing__skipped-view-all-link"
              onClick={onSkippedClick}
            >
              {t('view-all')}
            </LinkComponent>
          )}
        </div>
      </div>
      <div className={entityViewStyles.infoItem}>
        <div className={entityViewStyles.infoItemLabel}>
          <ChipIconCircle color={Colors.Orange} />
          <Typography variant="caption">{t('pending')}</Typography>
        </div>
        <div className={entityViewStyles.infoItemContent}>
          <Typography variant="body">
            {formatCount(coverage?.pending, i18n.language)}
          </Typography>
        </div>
      </div>
      <div className={entityViewStyles.infoItem}>
        <div className={entityViewStyles.infoItemLabel}>
          <ChipIconCircle color={Colors.Red} />
          <Typography variant="caption">{t('failed')}</Typography>
        </div>
        <div
          className={classNames(
            entityViewStyles.infoItemContent,
            styles.infoItemContentSpread,
          )}
        >
          <Typography variant="body">
            {formatCount(coverage?.failed, i18n.language)}
          </Typography>
          {!!coverage?.failed && onFailedClick && (
            <LinkComponent
              size={LinkSizes.small}
              data-test-id="ai-indexing__failed-view-all-link"
              onClick={onFailedClick}
            >
              {t('view-all')}
            </LinkComponent>
          )}
        </div>
      </div>
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="draft" symbolsOutlined />
          <Typography variant="caption">{t('total')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          <Typography variant="body">
            {formatCount(coverage?.total, i18n.language)}
          </Typography>
        </div>
      </div>
      {/* Everything the bar cannot say — whether anything is still working on
          what it has not counted, and whether some of it has been given up on —
          hangs under the bar as notes rather than as labelled rows of its own:
          both are exceptions, not steady state. */}
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          {indexingStatus?.isWorking ? (
            <MaterialIcon
              icon="progress_activity"
              symbolsOutlined
              className={styles.reindexingSpinner}
            />
          ) : (
            <MaterialIcon icon="data_usage" symbolsOutlined />
          )}
          <Typography variant="caption">{t('ai-indexing-progress')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          <div className={styles.usageRow}>
            <PercentageBarCell percentage={coverage?.progress} neutral />
            {/* How much of the remaining work is left, beside the share already
                done — the two halves of the same sentence. */}
            {(!!staleCount || !!reindexEta) && (
              <div className={styles.reindexNotes}>
                {!!staleCount && (
                  <>
                    <span className={styles.reindexNoteDivider} />
                    <span className={styles.reindexNote}>
                      <MaterialIcon icon="draft" symbolsOutlined size="md-16" />
                      <Typography variant="caption">
                        {t('ai-reindex-files-left', { count: staleCount })}
                      </Typography>
                    </span>
                  </>
                )}
                {!!reindexEta && (
                  <>
                    <span className={styles.reindexNoteDivider} />
                    <span className={styles.reindexNote}>
                      <MaterialIcon
                        icon="schedule"
                        symbolsOutlined
                        size="md-16"
                      />
                      <Typography variant="caption">
                        {t('ai-reindex-eta', { duration: reindexEta })}
                      </Typography>
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Spans both grid columns, so the notes run the full width of the row
            beneath the bar instead of squeezing in beside it. */}
        <IndexingStatusMessages
          className={entityViewStyles.infoItemMessage}
          storageId={storageId}
          status={indexingStatus}
          parkedCount={parkedCount}
          parkedReasons={coverage?.parkedReasons}
          lastIndexingAttemptAt={lastIndexingAttemptAt}
          onRetryParked={onRetryParked}
          retryParkedLoading={retryParkedLoading}
          onParkedClick={onParkedClick}
        />
      </div>
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="check_circle" symbolsOutlined />
          <Typography variant="caption">{t('ai-last-indexed')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          <Typography variant="body">
            {lastIndexedAt
              ? getFormattedDate(lastIndexedAt, dateTimeFormatEU)
              : '-'}
          </Typography>
        </div>
      </div>
      {/* The sign of life "Last indexed" cannot give: a backlog that keeps
          failing never completes, so only the attempt timestamp moves. */}
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="history" symbolsOutlined />
          <Typography variant="caption">{t('ai-last-attempt')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          <Typography variant="body">
            {lastIndexingAttemptAt
              ? getFormattedDate(lastIndexingAttemptAt, dateTimeFormatEU)
              : '-'}
          </Typography>
        </div>
      </div>
    </>
  );
}
